import React from "react";
import Navbar from "./Navbar";
import SEO from "../components/SEO";

// Curated aesthetic metrics
const STATS = [
  { value: "3-Wavelength", label: "Ice-Cooling Diode", sub: "755nm • 808nm • 1064nm" },
  { value: "Type I – VI", label: "Skin Tone Mapping", sub: "Fitzpatrick scale calibrated" },
  { value: "100%", label: "At-Home Privacy", sub: "Doorstep clinical sessions" },
  { value: "0 ms", label: "Thermal Downtime", sub: "Continuous contact cooling" },
];

const PILLARS = [
  {
    tag: "Protocol 01",
    title: "Triple-Wavelength Diode",
    desc: "Targeting coarse and fine follicles simultaneously across depths without surface heat damage or localized hyperpigmentation.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18M3 12h18" strokeDasharray="2 2" />
        <circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity="0.2" />
      </svg>
    ),
  },
  {
    tag: "Protocol 02",
    title: "Fitzpatrick Skin Calibration",
    desc: "Energy fluence (J/cm²) and pulse duration are individually adapted to melanin concentration prior to each treatment pass.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
      </svg>
    ),
  },
  {
    tag: "Protocol 03",
    title: "Hospital-Grade Hygiene",
    desc: "Pre-sealed sanitized applicator tips, disposable medical gowns, and clinical-grade disinfectant wipedowns before session launch.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
];

const FAQS = [
  {
    q: "Is laser hair removal genuinely painless?",
    a: "We deploy modern contact-cooling tips chilled to -4°C that numb epidermal layers upon contact, turning what used to be a rubber-band sting into a light, cooling sensation.",
  },
  {
    q: "How many sessions are typically required?",
    a: "Hair follicles cycle through anagen (growth), catagen, and telogen phases. Permanent reduction takes 6 to 8 sessions to systematically catch all follicles during active growth.",
  },
  {
    q: "How do at-home appointments work?",
    a: "Our certified aesthetician arrives equipped with a sanitized portable clinical diode setup and sterile disposables, transforming your private room into a sterile treatment space.",
  },
];

export default function AboutUs() {
  return (
    <div style={{ backgroundColor: "#FBFBFA", color: "#8dcae4", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
      <SEO
        title="About FlawSkin | Clinical Laser Hair Reduction"
        description="FlawSkin Private Limited delivers safe, painless, and dermatologically calibrated laser hair reduction with medical-grade equipment."
        canonical="https://flawskin.com/about"
      />
      <Navbar />

      {/* Hero Header with Organic Lighting Background */}
      <section className="position-relative overflow-hidden py-5 border-bottom border-light-subtle" style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)" }}>
        <div 
          className="position-absolute rounded-circle opacity-50"
          style={{
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(13,148,136,0.08) 0%, transparent 70%)",
            top: "-150px",
            left: "50%",
            transform: "translateX(-50%)",
            filter: "blur(60px)",
            pointerEvents: "none"
          }}
        />

        <div className="container py-lg-4 text-center position-relative">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-4 border" style={{ backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}>
            <span className="badge rounded-pill" style={{ backgroundColor: "#8dcae4", color: "#FFFFFF", fontSize: "0.72rem" }}>CLINICAL CARE</span>
            <span className="text-secondary small fw-medium">Flawskin Private Limited</span>
          </div>

          <h1 className="display-4 fw-bolder tracking-tight mb-3" style={{ color: "#0F172A", letterSpacing: "-0.03em" }}>
            Dermatological Precision. <br />
            <span style={{ color: "#8dcae4" }}>Uncompromised Skin Safety.</span>
          </h1>

          <p className="lead mx-auto mb-4" style={{ maxWidth: "660px", color: "#475569", lineHeight: "1.7", fontSize: "1.1rem" }}>
            We engineered Flawskin to replace the overpriced clinic visits and painful waxing routines with hospital-grade, painless laser hair reduction performed in personal comfort.
          </p>

          <div className="d-flex flex-wrap justify-content-center gap-3">
            <a 
              href="#founder-story" 
              className="btn px-4 py-2.5 rounded-3 fw-semibold text-white shadow-sm"
              style={{ backgroundColor: "#0F172A", border: "none" }}
            >
              Meet Our Specialist
            </a>
            <a 
              href="/services" 
              className="btn px-4 py-2.5 rounded-3 fw-semibold bg-white border shadow-sm"
              style={{ color: "#334155", borderColor: "#CBD5E1" }}
            >
              Explore Treatments
            </a>
          </div>
        </div>
      </section>

      {/* Precision Stats Bar */}
      <section className="bg-white border-bottom">
        <div className="container py-4">
          <div className="row g-4 text-center">
            {STATS.map((stat, i) => (
              <div key={i} className="col-6 col-lg-3">
                <div className="p-2">
                  <div className="fw-bolder fs-4" style={{ color: "#0F172A", letterSpacing: "-0.02em" }}>{stat.value}</div>
                  <div className="fw-semibold small mt-1" style={{ color: "#8dcae4" }}>{stat.label}</div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>{stat.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story + Practitioner Column */}
      <section className="py-5" id="founder-story">
        <div className="container py-lg-4">
          <div className="row g-4 align-items-stretch">
            
            {/* Story & Vision */}
            <div className="col-12 col-lg-7 d-flex flex-column gap-4">
              <div className="p-4 p-md-5 rounded-4 bg-white border shadow-sm h-100 d-flex flex-column justify-content-center" style={{ borderColor: "#E2E8F0" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="p-2 rounded-2" style={{ backgroundColor: "#CCFBF1", color: "#0F766E" }}>
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                      <path d="M12 7v5l3 3" />
                    </svg>
                  </span>
                  <span className="text-uppercase fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.08em", color: "#8dcae4" }}>Our Origin</span>
                </div>
                <h2 className="h3 fw-bold mb-3" style={{ color: "#0F172A" }}>Built on the conviction that aesthetic care should be transparent and painless.</h2>
                <p className="mb-3" style={{ color: "#475569", lineHeight: "1.75", fontSize: "0.95rem" }}>
                  Incorporated in 2024, Flawskin Private Limited was established to address an industry standard: clinical clinics that charge exorbitant fees while treating individuals as rapid-turnover appointments rather than unique skin profiles.
                </p>
                <p className="mb-0" style={{ color: "#475569", lineHeight: "1.75", fontSize: "0.95rem" }}>
                  By combining high-output, triple-wavelength diode lasers with direct-to-home convenience, our certified technicians customize every session to individual pain tolerance, hair depth, and melanin variations.
                </p>
              </div>

              {/* Vision Card */}
              <div className="p-4 rounded-4 border shadow-sm d-flex flex-column justify-content-between" style={{ backgroundColor: "#0F172A", borderColor: "#1E293B", color: "#F8FAFC" }}>
                <div>
                  <span className="badge px-3 py-1.5 rounded-pill mb-3" style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "#8dcae4", fontSize: "0.72rem" }}>OUR PROMISE</span>
                  <h3 className="h5 fw-bold text-white mb-2">Zero Burns. Zero Guesswork. Complete Efficacy.</h3>
                  <p className="small mb-0" style={{ color: "#94A3B8", lineHeight: "1.7" }}>
                    We never compromise on safety. If our diagnostic patch test indicates your skin isn't ready for a given fluence setting, we calibrate the energy profile until it is completely safe.
                  </p>
                </div>
              </div>
            </div>

            {/* Practitioner Profile */}
            <div className="col-12 col-lg-5">
              <div className="p-4 p-md-5 rounded-4 bg-white border shadow-sm h-100 d-flex flex-column justify-content-between text-center" style={{ borderColor: "#E2E8F0" }}>
                <div>
                  <div className="position-relative d-inline-block mx-auto mb-4">
                    <img
                      src="assets/assets/img/therapist-founder.jpg"
                      alt="Bandi Nandhini - Skin Therapist & Practitioner"
                      className="rounded-circle shadow-sm"
                      style={{
                        width: "140px",
                        height: "140px",
                        objectFit: "cover",
                        border: "4px solid #F1F5F9"
                      }}
                      onError={(e) => {
                        // Fallback avatar in case of missing image path
                        e.target.src = "https://images.unsplash.com/photo-1594824813576-9c44e99f6608?auto=format&fit=crop&w=300&q=80";
                      }}
                    />
                    <span 
                      className="position-absolute bottom-0 start-50 translate-middle-x badge px-3 py-1 rounded-pill text-white shadow-sm"
                      style={{ backgroundColor: "#8dcae4", fontSize: "0.7rem", letterSpacing: "0.03em" }}
                    >
                      PRACTITIONER
                    </span>
                  </div>

                  <h3 className="h5 fw-bold mb-1" style={{ color: "#0F172A" }}>Bandi Nandhini</h3>
                  <p className="small fw-semibold mb-3" style={{ color: "#8dcae4" }}>Senior Skin Therapist</p>
                  
                  <p className="small text-start px-md-2 mb-4" style={{ color: "#475569", lineHeight: "1.7" }}>
                    With comprehensive practical expertise in diode light pulse modulation and dermatological hygiene protocols, Nandhini leads the clinical standards and treatment setups for every Flawskin client.
                  </p>
                </div>

                <div className="rounded-3 p-3 text-start border" style={{ backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" }}>
                  <div className="d-flex align-items-center gap-2 mb-2 text-dark small">
                    <svg width="16" height="16" fill="none" stroke="#8dcae4" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="fw-medium" style={{ color: "#334155" }}>Laser Biophysics & Safety Certified</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2 text-dark small">
                    <svg width="16" height="16" fill="none" stroke="#8dcae4" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="fw-medium" style={{ color: "#334155" }}>Fitzpatrick Scale III-V Depth Calibration</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 text-dark small">
                    <svg width="16" height="16" fill="none" stroke="#8dcae4" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="fw-medium" style={{ color: "#334155" }}>Single-Use Sterilization Adherence</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Clinical Care Pillars */}
      <section className="py-5 border-top border-bottom bg-white">
        <div className="container py-lg-4">
          <div className="text-center mb-5">
            <span className="text-uppercase fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.08em", color: "#8dcae4" }}>Clinical Infrastructure</span>
            <h2 className="h3 fw-bold mt-1" style={{ color: "#0F172A" }}>How We Protect Your Dermal Barrier</h2>
            <p className="mx-auto text-secondary small" style={{ maxWidth: "540px" }}>
              Laser hair reduction is an exact science. We pair advanced medical equipment with precise operational checklists.
            </p>
          </div>

          <div className="row g-4">
            {PILLARS.map((p, i) => (
              <div key={i} className="col-12 col-md-4">
                <div 
                  className="p-4 rounded-4 border h-100 d-flex flex-column justify-content-between transition"
                  style={{ backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}
                >
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <span className="p-2.5 rounded-3" style={{ backgroundColor: "#F0FDFA", color: "#8dcae4" }}>
                        {p.icon}
                      </span>
                      <span className="badge rounded-pill fw-medium" style={{ backgroundColor: "#F1F5F9", color: "#64748B", fontSize: "0.7rem" }}>
                        {p.tag}
                      </span>
                    </div>
                    <h3 className="h6 fw-bold mb-2" style={{ color: "#0F172A" }}>{p.title}</h3>
                    <p className="small mb-0" style={{ color: "#64748B", lineHeight: "1.7" }}>{p.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-5" style={{ backgroundColor: "#F8FAFC" }}>
        <div className="container py-lg-4">
          <div className="text-center mb-5">
            <span className="text-uppercase fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.08em", color: "#8dcae4" }}>Clarity & Answers</span>
            <h2 className="h3 fw-bold mt-1" style={{ color: "#0F172A" }}>Frequently Answered Questions</h2>
          </div>

          <div className="row justify-content-center">
            <div className="col-12 col-lg-8 d-flex flex-column gap-3">
              {FAQS.map((faq, index) => (
                <div key={index} className="p-4 rounded-3 bg-white border shadow-xs" style={{ borderColor: "#E2E8F0" }}>
                  <h4 className="h6 fw-bold mb-2" style={{ color: "#0F172A" }}>{faq.q}</h4>
                  <p className="small mb-0" style={{ color: "#64748B", lineHeight: "1.7" }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Minimalist Dark Call to Action */}
      <section className="py-5" style={{ backgroundColor: "#0F172A" }}>
        <div className="container py-lg-3 text-center">
          <h2 className="display-6 fw-bold text-white mb-3" style={{ letterSpacing: "-0.02em" }}>Experience Dermatological Care at Home</h2>
          <p className="mx-auto mb-4" style={{ maxWidth: "520px", color: "#94A3B8", fontSize: "0.95rem" }}>
            Book a test patch or schedule your full session with sterile medical tools and personalized parameters.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <a 
              href="/contact" 
              className="btn px-4 py-2.5 rounded-3 fw-semibold text-dark shadow-sm"
              style={{ backgroundColor: "#5EEAD4", border: "none" }}
            >
              Book Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
